import React from "react";
import { Table, Tag, Tooltip, Typography } from "antd";
import {
  SendOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  LinkOutlined,
  WarningOutlined,
  StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Text } = Typography;

const eventTypeConfig = {
  Send: { color: "blue", icon: <SendOutlined />, label: "Enviado" },
  Delivery: { color: "green", icon: <CheckCircleOutlined />, label: "Entregue" },
  Open: { color: "cyan", icon: <EyeOutlined />, label: "Aberto" },
  Click: { color: "purple", icon: <LinkOutlined />, label: "Clique" },
  Bounce: { color: "orange", icon: <WarningOutlined />, label: "Bounce" },
  Complaint: { color: "red", icon: <StopOutlined />, label: "Reclamacao" },
  Reject: { color: "magenta", icon: <StopOutlined />, label: "Rejeitado" },
};

const EventsTable = ({ events, loading, pagination, onPageChange }) => {
  const columns = [
    {
      title: "Tipo",
      dataIndex: "eventType",
      key: "eventType",
      width: 120,
      render: (type) => {
        const config = eventTypeConfig[type] || {
          color: "default",
          icon: null,
          label: type,
        };
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: "Destinatario",
      dataIndex: "destination",
      key: "destination",
      width: 200,
      render: (dest) => (
        <Tooltip title={Array.isArray(dest) ? dest.join(", ") : dest}>
          <Text ellipsis style={{ maxWidth: 180 }}>
            {Array.isArray(dest) ? dest[0] : dest}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Assunto",
      dataIndex: "subject",
      key: "subject",
      ellipsis: true,
      render: (subject) => (
        <Tooltip title={subject}>
          <Text ellipsis style={{ maxWidth: 250 }}>
            {subject || "-"}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Remetente",
      dataIndex: "source",
      key: "source",
      width: 180,
      render: (source) => (
        <Tooltip title={source}>
          <Text ellipsis style={{ maxWidth: 160 }}>
            {source}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Data/Hora",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 150,
      render: (timestamp) => dayjs(timestamp).format("DD/MM/YY HH:mm"),
      sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
    },
    {
      title: "Detalhes",
      key: "details",
      width: 150,
      render: (_, record) => {
        if (record.eventType === "Open" || record.eventType === "Click") {
          return (
            <Tooltip
              title={
                <div>
                  <div>IP: {record.ipAddress || "-"}</div>
                  <div>UA: {record.userAgent?.substring(0, 50) || "-"}</div>
                  {record.link && <div>Link: {record.link}</div>}
                </div>
              }
            >
              <Text type="secondary" style={{ cursor: "pointer" }}>
                {record.ipAddress || "Ver detalhes"}
              </Text>
            </Tooltip>
          );
        }
        if (record.eventType === "Bounce") {
          return (
            <Tooltip title={`${record.bounceType} - ${record.bounceSubType}`}>
              <Tag color="orange">{record.bounceType || "Bounce"}</Tag>
            </Tooltip>
          );
        }
        if (record.eventType === "Complaint") {
          return (
            <Tag color="red">{record.complaintType || "Complaint"}</Tag>
          );
        }
        return "-";
      },
    },
  ];

  const expandedRowRender = (record) => (
    <div style={{ padding: "8px 16px", background: "#fafafa" }}>
      <p>
        <strong>Message ID:</strong> {record.messageId}
      </p>
      {record.ipAddress && (
        <p>
          <strong>IP:</strong> {record.ipAddress}
        </p>
      )}
      {record.userAgent && (
        <p>
          <strong>User Agent:</strong> {record.userAgent}
        </p>
      )}
      {record.link && (
        <p>
          <strong>Link:</strong> {record.link}
        </p>
      )}
      {record.bounceType && (
        <p>
          <strong>Bounce:</strong> {record.bounceType} / {record.bounceSubType}
        </p>
      )}
    </div>
  );

  return (
    <Table
      columns={columns}
      dataSource={events}
      loading={loading}
      rowKey={(record) => record.id || record._id || record.messageId + record.eventType}
      expandable={{
        expandedRowRender,
        rowExpandable: (record) =>
          record.messageId ||
          record.ipAddress ||
          record.userAgent ||
          record.bounceType,
      }}
      pagination={{
        current: pagination.page,
        pageSize: pagination.limit,
        total: pagination.total,
        showSizeChanger: true,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} de ${total} eventos`,
        pageSizeOptions: ["25", "50", "100"],
        onChange: (page, pageSize) => onPageChange(page, pageSize),
      }}
      size="middle"
      scroll={{ x: 900 }}
    />
  );
};

export default EventsTable;
